/** React imports */
import React, { FunctionComponent, lazy } from 'react';
/** App imports */
import Loading from '../Components/Loading';
import ProjectValidator from '../utils/components/ProjectValidator';
import AdminValidator from '../utils/components/AdminValidator';
import CompanyValidator from '../utils/components/CompanyValidator';
import NewVisualizerQueryParamsValidator from '../utils/components/NewVisualizerQueryParamsValidator';
import DrillholeImageValidator from '../utils/components/DrillholeImageValidator';
import LogIdValidator from '../utils/components/LogIdValidator';
import SubscriptionAndFeatureValidator from '../utils/components/SubscriptionAndFeatureValidator';
import { navigationItems } from '../utils/navigationItems';
import { VisualizerV3GlobalDataValidator } from '../Components/Pages/VisualizerV3/validators/VisualizerV3GlobalDataValidator';

const ProjectsDashboard = lazy(
  () => import('../Components/Pages/ProjectsDashboard'),
);
const ProjectDetailsPage = lazy(
  () => import('../Components/Pages/Projects>Details'),
);
const CleanImages = lazy(
  () => import('../Components/Pages/Projects>Clean-Images'),
);
const Desurvey = lazy(() => import('../Components/Pages/Projects>Desurvey'));
const Drillholes = lazy(
  () => import('../Components/Pages/Projects>Details>Drillholes'),
);
const DrillholeDetails = lazy(
  () => import('../Components/Pages/Projects>Details>Drillholes>Details'),
);
const ViewCollarsPage = lazy(
  () => import('../Components/Pages/Projects>Details>Collars'),
);
const AddCollarsPage = lazy(
  () => import('../Components/Pages/Projects>Details>Collars>Add'),
);
const ViewSurveysPage = lazy(
  () => import('../Components/Pages/Projects>Details>Surveys'),
);
const AddSurveysPage = lazy(
  () => import('../Components/Pages/Projects>Details>Surveys>Add'),
);
const ProjectSettingsPage = lazy(
  () => import('../Components/Pages/Projects>ProjectSettings'),
);
const RoQENet = lazy(() => import('../Components/Pages/Projects>RoQENet'));
const VeinNetPage = lazy(() => import('../Components/Pages/Projects>VeinNET'));
const DrillholeValidator = lazy(
  () => import('../utils/components/DrillholeValidator'),
);
const ProjectExportsPage = lazy(
  () => import('../Components/Pages/Projects>Exports'),
);
const TextureReport = lazy(
  () => import('../Components/Pages/Projects>TextureReport'),
);
const VeinFrequency = lazy(
  () => import('../Components/Pages/Projects>VeinFrequency'),
);
// const ColorCluster = lazy(
//   () => import('../Components/Pages/Projects>ColorCluster'),
// );
// const PercentageReport = lazy(
//   () => import('../Components/Pages/Projects>PercentageReport'),
// );
const MunsellReport = lazy(
  () => import('../Components/Pages/Projects>MunsellReport'),
);
// const ColorExtraction = lazy(
//   () => import('../Components/Pages/Projects>ColorExtraction'),
// );
const RQD = lazy(() => import('../Components/Pages/Projects>RQD'));
const FracturePage = lazy(
  () => import('../Components/Pages/Projects>Fracture'),
);
const Inconsistencies = lazy(
  () => import('../Components/Pages/Projects>ImagesQAQC'),
);
const EditMaskPage = lazy(
  () => import('../Components/Pages/EditMask/EditMask'),
);
const BulkUpload = lazy(() => import('../Components/Pages/BulkUpload'));
const UserDetailsPage = lazy(
  () => import('../Components/Pages/UserDetailPage'),
);
const ViewIntervalsPage = lazy(
  () => import('../Components/Pages/Projects>Details>Interval'),
);
const AddIntervalsPage = lazy(
  () => import('../Components/Pages/Projects>Details>Intervals>Add'),
);
const FCNetPage = lazy(() => import('../Components/Pages/Projects>FCNet'));
const ImagesCoreLengthSummary = lazy(
  () => import('../Components/Pages/Projects>ImagesQAQC>CoreLengthSummary'),
);
const Visualizer = lazy(() => import('../Components/Pages/Visualizer'));
const VisualizerV3 = lazy(() => import('../Components/Pages/VisualizerV3'));
const UploadLogs = lazy(() => import('../Components/Pages/UploadLogs'));
const ViewLogsPage = lazy(() => import('../Components/Pages/ViewLogs'));
const PredictPage = lazy(() => import('../Components/Pages/Projects>Predict'));
const Merge = lazy(() => import('../Components/Pages/ProjectsMergePage'));
const SmoothDataPage = lazy(
  () => import('../Components/Pages/Projects>SmoothData'),
);
const TrainModel = lazy(() => import('../Components/Pages/TrainModel'));
const TrainModelDetails = lazy(
  () => import('../Components/Pages/TrainModelDetails'),
);
const ModelLibrary = lazy(() => import('../Components/Pages/ModelLibrary'));
const ModelLibraryItem = lazy(
  () => import('../Components/Pages/ModelLibraryItem'),
);
const DownloadImages = lazy(() => import('../Components/Pages/DownloadImages'));
const Redirect = lazy(() => import('../Components/Pages/Redirect'));
const NoMatch = lazy(() => import('../Components/404'));
const UsageReport = lazy(() => import('../Components/Pages/UsageReport'));
const ThreeDViewer = lazy(() => import('../Components/Pages/3DViewer'));

// Some folks find value in a centralized route config.
// A route config is just data. React is great at mapping
// data into components, and <Route> is a component.

// Our route config is just an array of logical "routes"
// with `path` and `element` props, ordered the same
// way you'd do inside a `<Routes>`.

export interface RouteConfigInterface {
  path: string;
  component: JSX.Element;
  id: string;
  /**
   * List of validator components to wrap route component in.
   *
   * Applied in order of outermost to innermost.
   */
  validators: FunctionComponent<any>[];
  exact?: boolean;
}

// order matters here; more specific routes come first
const baseRoutes: Array<RouteConfigInterface> = [
  {
    path: '/',
    component: <ProjectsDashboard />,
    validators: [CompanyValidator],
    id: 'home-route',
  },
  {
    path: '/redirect',
    exact: true,
    validators: [],
    component: <Redirect />,
    id: 'redirect-route',
  },
  {
    path: '/:companyId',
    component: <ProjectsDashboard />,
    validators: [CompanyValidator],
    id: 'projects-dashboard-route',
  },
  {
    path: '/:companyId/users/:userId',
    exact: true,
    validators: [CompanyValidator],
    component: <UserDetailsPage />,
    id: 'user-detail-page-route',
  },
];

const projectPrefix: string = '/:companyId/projects/:projectId';

const projectRoutes: Array<RouteConfigInterface> = [
  {
    path: projectPrefix + navigationItems['project-dashboard'].path,
    component: <ProjectDetailsPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['project-dashboard'].key,
  },
  {
    path: `${projectPrefix}/settings`,
    component: <ProjectSettingsPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
      AdminValidator,
    ],
    id: navigationItems['project-settings'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-upload-images'].path,
    component: <BulkUpload />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-upload-images'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-cleanimages'].path,
    component: <CleanImages />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-cleanimages'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-desurvey'].path,
    component: <Desurvey />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-desurvey'].key,
  },
  // Not currently offered to customers, but leaving here for future use
  // {
  //   path: `${projectPrefix}/custom-color-report/color-cluster`,
  //   component: <ColorCluster />,
  //   validators: [CompanyValidator, SubscriptionAndFeatureValidator, ProjectValidator],
  //   id: 'projects-color-cluster',
  // },
  {
    path: projectPrefix + navigationItems['projects-roqenet'].path,
    component: <RoQENet />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-roqenet'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-rqd'].path,
    component: <RQD />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-rqd'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-veinnet'].path,
    component: <VeinNetPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-veinnet'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-fracture'].path,
    component: <FracturePage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-fracture'].key,
  },
  // Not currently offered to customers, but leaving here for future use
  // {
  //   path: `${projectPrefix}/custom-color-report/color-extraction`,
  //   component: <ColorExtraction />,
  //   validators: [CompanyValidator, SubscriptionAndFeatureValidator, ProjectValidator],
  //   id: 'projects-color-extraction',
  // },
  // Not currently offered to customers, but leaving here for future use
  // {
  //   path: `${projectPrefix}/custom-color-report/percentage`,
  //   component: <PercentageReport />,
  //   validators: [CompanyValidator, SubscriptionAndFeatureValidator, ProjectValidator],
  //   id: 'projects-percentage',
  // },
  {
    path: projectPrefix + navigationItems['projects-munsell'].path,
    component: <MunsellReport />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-munsell'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-texture'].path,
    component: <TextureReport />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-texture'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-veinfrequency'].path,
    component: <VeinFrequency />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-veinfrequency'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-fcnet'].path,
    component: <FCNetPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-fcnet'].key,
  },
  // TODO: Enable when we allow boxynet
  // {
  //   path: `${projectPrefix}/boxynet`,
  //   component: <BoXYNet />,
  //   validators: [CompanyValidator, ProjectValidator],
  //   id: 'boxynet-page-route',
  // },
  {
    path: projectPrefix + navigationItems['projects-collar'].path,
    component: <ViewCollarsPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-collar'].key,
  },
  {
    path: projectPrefix + navigationItems['add-collar-data'].path,
    component: <AddCollarsPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['add-collar-data'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-survey'].path,
    component: <ViewSurveysPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-survey'].key,
  },
  {
    path: projectPrefix + navigationItems['add-survey-data'].path,
    component: <AddSurveysPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['add-survey-data'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-interval'].path,
    component: <ViewIntervalsPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-interval'].key,
  },
  {
    path: projectPrefix + navigationItems['add-custom-intervals'].path,
    component: <AddIntervalsPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['add-custom-intervals'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-add-logs'].path,
    component: <UploadLogs />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-add-logs'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-visual'].path,
    component: <Visualizer />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
      NewVisualizerQueryParamsValidator,
    ],
    id: navigationItems['projects-visual'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-visual-v3'].path,
    component: <VisualizerV3 />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
      VisualizerV3GlobalDataValidator,
    ],
    id: navigationItems['projects-visual-v3'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-export'].path,
    component: <ProjectExportsPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-export'].key,
  },
  {
    path:
      projectPrefix +
      navigationItems['projects-view-logs'].path +
      '/:logId?',
    component: <ViewLogsPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
      LogIdValidator,
    ],
    id: navigationItems['projects-view-logs'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-download-images'].path,
    component: <DownloadImages />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-download-images'].key,
  },
  {
    path:
      projectPrefix +
      navigationItems['projects-images-gaps-overlaps-unprocessed'].path,
    component: <Inconsistencies />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-images-gaps-overlaps-unprocessed'].key,
  },
  {
    path: projectPrefix + navigationItems['projects-images-core-length'].path,
    component: <ImagesCoreLengthSummary />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-images-core-length'].key,
  },
  {
    path: projectPrefix + navigationItems['test-feature'].path,
    component: <ThreeDViewer />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['test-feature'].key,
  },
  {
    path: projectPrefix + navigationItems['run-prediction'].path,
    component: <PredictPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['run-prediction'].key,
  },
  {
    path: projectPrefix + navigationItems['merge-data'].path,
    component: <Merge />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['merge-data'].key,
  },
  {
    path: projectPrefix + navigationItems['smooth-data'].path,
    component: <SmoothDataPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['smooth-data'].key,
  },
  {
    path: projectPrefix + navigationItems['billing-usage-report'].path,
    component: <UsageReport />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['billing-usage-report'].key,
  },
  {
    path: projectPrefix + navigationItems['train-model'].path,
    component: <TrainModel />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['train-model'].key,
  },
  {
    path: projectPrefix + navigationItems['model-library'].path,
    component: <ModelLibrary />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['model-library'].key,
  },
  {
    path: `${projectPrefix}/model-library/:objectId`,
    component: <ModelLibraryItem />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: 'project-model-library-item',
  },
  {
    path: `${projectPrefix}/train-model/:jobId`,
    component: <TrainModelDetails />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: 'project-train-model-details-route',
  },
];

const drillholePrefix: string = `${projectPrefix}/drillholes`;

const drillholeRoutes: Array<RouteConfigInterface> = [
  {
    path: projectPrefix + navigationItems['projects-drillholes'].path,
    component: <Drillholes />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
    ],
    id: navigationItems['projects-drillholes'].key,
  },
  {
    path: `${drillholePrefix}/:drillholeId`,
    component: <DrillholeDetails />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
      DrillholeValidator,
    ],
    id: 'drillhole-detail-page-route',
  },
  {
    path: `${drillholePrefix}/:drillholeId/images/:drillholeImageId/rectify`,
    component: <EditMaskPage />,
    validators: [
      CompanyValidator,
      SubscriptionAndFeatureValidator,
      ProjectValidator,
      DrillholeValidator,
      DrillholeImageValidator,
    ],
    id: 'drillhole-detail-images-edit-image',
  },
];

const errorRoute: RouteConfigInterface = {
  // 404 catch-all
  path: '*',
  exact: false,
  component: (
    <React.Suspense fallback={<Loading />}>
      <NoMatch />
    </React.Suspense>
  ),
  validators: [],
  id: '404-route',
};

export const routeConfig: Array<RouteConfigInterface> = [
  ...baseRoutes,
  ...projectRoutes,
  ...drillholeRoutes,
  errorRoute,
];
